import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/utils/api'

export type SubscriptionTier = 'free' | 'basic' | 'monthly' | 'annual' | 'vip'

export interface AieRoleData {
  aieRole: 'super_admin' | 'admin' | 'user'
  subscriptionTier: SubscriptionTier
  subscriptionExpiry: string | null
  /** 无限畅用（计费豁免 + 模型全解锁）；localStorage 降级路径恒 false，me 请求成功后为准 */
  isUnlimited: boolean
}

const SUPER_ADMIN_EMAILS = ['ddsteed@gmail.com']

/** 从 localStorage 读取角色（登录时写入） */
function getStoredRole(): AieRoleData {
  try {
    const storage = localStorage.getItem('aie-user-storage')
    if (storage) {
      const parsed = JSON.parse(storage)
      const metadata = parsed?.state?.user?.metadata
      const email = parsed?.state?.user?.email || ''

      // 优先用登录时从后端获取的角色
      if (metadata?.aieRole === 'super_admin' || metadata?.aieRole === 'admin' || metadata?.aieRole === 'user') {
        return {
          aieRole: metadata.aieRole,
          subscriptionTier: (metadata?.subscriptionTier as SubscriptionTier) || 'free',
          subscriptionExpiry: metadata?.subscriptionExpiry ?? null,
          isUnlimited: metadata?.isUnlimited === true,
        }
      }

      // 降级：用邮箱匹配
      if (SUPER_ADMIN_EMAILS.includes(email.toLowerCase())) {
        return { aieRole: 'super_admin', subscriptionTier: 'free', subscriptionExpiry: null, isUnlimited: false }
      }
    }
  } catch { /* ignore */ }
  return { aieRole: 'user', subscriptionTier: 'free', subscriptionExpiry: null, isUnlimited: false }
}

export function useAieRole() {
  const [data, setData] = useState<AieRoleData>(getStoredRole)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const res = await apiClient.get<AieRoleData>('/aie/account/me')
      setData({ ...res, isUnlimited: res.isUnlimited === true })
    } catch {
      // API 失败时保留 localStorage 的角色（登录时写入）
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const isAdmin = data.aieRole === 'super_admin' || data.aieRole === 'admin'
  // 泡泡条显隐用：仅超级管理员隐藏（admin 照常显示 ∞ 无限畅用态）；isAdmin 保持管理语义（后台入口等）
  const isSuperAdmin = data.aieRole === 'super_admin'

  return { ...data, isAdmin, isSuperAdmin, loading, refresh }
}
